import React, { useState, useEffect, memo, useCallback } from 'react';
import {
  Layout,
  Typography,
  Card,
  Space,
  Button,
  Modal,
  Form,
  notification,
  Avatar,
  Badge,
  Tooltip,
  Progress,
  Tag,
  Row,
  Col,
  Switch,
  Alert,
  Divider,
  Input,
  Upload,
  Menu,
  Dropdown
} from 'antd';
import {
  SafetyOutlined,
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  TeamOutlined,
  ContactsOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  BellOutlined,
  LockOutlined,
  EyeOutlined,
  UploadOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// Modern theme configuration with gradients
const theme = {
  colors: {
    primary: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    secondary: '#6b7280',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#60a5fa',
    neutral: '#4b5563',
    lightGray: '#f1f5f9',
    mediumGray: '#e2e8f0',
    darkGray: '#1f2937',
    white: '#ffffff',
    border: '#d1d5db',
    navHover: 'rgba(59, 130, 246, 0.1)',
    navActive: 'rgba(59, 130, 246, 0.2)'
  },
  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
    sm: '0 2px 4px 0 rgba(0, 0, 0, 0.08)',
    md: '0 3px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 8px 12px -2px rgba(0, 0, 0, 0.1)',
  },
  borderRadius: {
    sm: '6px',
    md: '10px',
    lg: '14px',
    xl: '18px'
  },
  fonts: {
    base: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }
};

// Refined styles for modern mobile view
const styles = {
  layout: {
    minHeight: '100vh',
    backgroundColor: theme.colors.lightGray,
    width: '100%',
    overflowX: 'hidden',
    fontFamily: theme.fonts.base,
  },
  header: {
    backgroundColor: theme.colors.white,
    padding: window.innerWidth <= 768 ? '0 8px' : '0 48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: theme.shadows.sm,
    borderBottom: `1px solid ${theme.colors.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    height: window.innerWidth <= 768 ? '56px' : '80px',
    borderRadius: window.innerWidth <= 768 ? '0 0 8px 8px' : '0 0 12px 12px',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: window.innerWidth <= 768 ? '6px' : '16px',
  },
  logoIcon: {
    width: window.innerWidth <= 768 ? '32px' : '48px',
    height: window.innerWidth <= 768 ? '32px' : '48px',
    borderRadius: theme.borderRadius.sm,
    background: theme.colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: window.innerWidth <= 768 ? '14px' : '24px',
    boxShadow: theme.shadows.xs,
  },
  logoText: {
    margin: 0,
    color: theme.colors.darkGray,
    fontWeight: 700,
    fontSize: window.innerWidth <= 768 ? '16px' : '26px',
    lineHeight: 1.3,
  },
  navContainer: {
    display: window.innerWidth <= 768 ? 'none' : 'flex',
    alignItems: 'center',
    gap: '8px',
    background: theme.colors.lightGray,
    padding: '6px 12px',
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.xs,
  },
  navItem: {
    color: theme.colors.neutral,
    fontWeight: 600,
    padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
    borderRadius: theme.borderRadius.sm,
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: window.innerWidth <= 768 ? '13px' : '15px',
    position: 'relative',
    overflow: 'hidden',
  },
  navItemHover: {
    background: theme.colors.navHover,
    color: theme.colors.darkGray,
  },
  navItemActive: {
    background: theme.colors.primary,
    color: 'white',
    boxShadow: theme.shadows.sm,
  },
  mobileMenuButton: {
    display: window.innerWidth <= 768 ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    borderRadius: theme.borderRadius.sm,
    background: theme.colors.lightGray,
    border: `1px solid ${theme.colors.border}`,
  },
  content: {
    padding: window.innerWidth <= 768 ? '8px' : '40px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  pageHeader: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: window.innerWidth <= 768 ? '8px' : '48px',
    marginBottom: window.innerWidth <= 768 ? '8px' : '32px',
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.border}`,
  },
  sectionCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: window.innerWidth <= 768 ? '8px' : '40px',
    marginBottom: window.innerWidth <= 768 ? '8px' : '24px',
    border: `1px solid ${theme.colors.border}`,
    boxShadow: theme.shadows.sm,
  },
  actionCard: {
    height: '100%',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    transition: 'all 0.2s ease',
    backgroundColor: theme.colors.white,
  },
  progressContainer: {
    backgroundColor: theme.colors.lightGray,
    borderRadius: theme.borderRadius.md,
    padding: window.innerWidth <= 768 ? '8px' : '24px',
    marginTop: window.innerWidth <= 768 ? '8px' : '24px',
  },
};

// Smooth animations for mobile
const animations = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  },
  slideUp: {
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -15 }
  },
  scale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }
  },
  mobileMenu: {
    initial: { opacity: 0, scaleY: 0 },
    animate: { opacity: 1, scaleY: 1 },
    exit: { opacity: 0, scaleY: 0 },
    transition: { duration: 0.2, ease: 'easeInOut' }
  }
};

// Custom hook for user data management
const useUserData = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch('/backend/get_profile.php', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        if (!response.ok) {
          if (response.status === 401) {
            navigate('/login');
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data.userName || data.nhGuideNumber) {
          const location = [data.street, data.city].filter(Boolean).join(', ') || 'Location not set';
          const mappedUserData = {
            name: data.userName || 'User',
            location: location,
            nhNumber: data.nh_guide_number || data.nhGuideNumber || 'Not assigned',
            avatar: data.profile_picture || data.profilePicture || null,
            notifications: data.notifications || 0,
            lastLogin: new Date().toLocaleDateString(),
          };
          setUserData(mappedUserData);
        } else {
          if (data.message && (data.message.toLowerCase().includes('not logged in') || data.message.toLowerCase().includes('authentication'))) {
            navigate('/login');
            return;
          }
          setUserData({
            name: 'Guest User',
            location: 'Location not available',
            nhNumber: 'Not assigned',
            avatar: null,
            notifications: 0,
            lastLogin: new Date().toLocaleDateString(),
          });
          notification.warning({
            message: 'Data Load Warning',
            description: data.message || 'Some user information could not be loaded',
            placement: 'topRight',
            duration: 3,
            onClose: () => navigate('/login'),
          });
        }
      } catch (err) {
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
          setError('Network error - please check your connection');
          notification.error({
            message: 'Network Error',
            description: 'Network error - please check your connection',
            placement: 'topRight',
            duration: 3,
          });
        } else if (err.message.includes('401')) {
          navigate('/login');
          return;
        } else {
          setUserData({
            name: 'Guest User',
            location: 'Location not available',
            nhNumber: 'Not assigned',
            avatar: null,
            notifications: 0,
            lastLogin: new Date().toLocaleDateString(),
          });
          notification.warning({
            message: 'Data Load Warning',
            description: 'Some user information could not be loaded',
            placement: 'topRight',
            duration: 3,
            onClose: () => navigate('/login'),
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [navigate]);
  return { userData, loading, error };
};

// Modern Header Component
const ModernHeader = memo(({ userData, loading, onLogout }) => {
  const navigate = useNavigate();
  const [menuVisible, setMenuVisible] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      const response = await fetch('/backend/logout.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        notification.success({
          message: 'Logout Successful',
          description: 'You have been successfully logged out.',
          placement: 'topRight',
        });
        navigate('/login');
      } else {
        notification.error({
          message: 'Logout Error',
          description: data.error || 'Error during logout',
          placement: 'topRight',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Connection Error',
        description: 'An error occurred during logout',
        placement: 'topRight',
      });
    }
  }, [navigate]);

  const menuItems = [
    { key: 'home', to: '/home', icon: <HomeOutlined />, label: 'Home' },
    { key: 'profile', to: '/profile', icon: <UserOutlined />, label: 'Profile' },
    { key: 'contact', to: '/ContactForm', icon: <ContactsOutlined />, label: 'Contact' },
    { key: 'services', to: '/ServiceProviders', icon: <TeamOutlined />, label: 'Services' },
    { key: 'privacy', to: '/DataPrivacy', icon: <SafetyOutlined />, label: 'Privacy', active: true },
  ];

  const mobileMenuItems = menuItems.map(item => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
    onClick: () => {
      navigate(item.to);
      setMenuVisible(false);
    },
    style: {
      fontSize: '13px',
      fontWeight: 500,
      padding: '8px 12px',
      margin: '4px 0',
      borderRadius: theme.borderRadius.sm,
      background: item.active ? theme.colors.navActive : 'transparent',
      color: item.active ? theme.colors.darkGray : theme.colors.neutral,
    },
  }));

  return (
    <motion.div
      style={styles.header}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <div style={styles.logoContainer}>
        <Avatar
          icon={<UserOutlined />}
          size={window.innerWidth <= 768 ? 32 : 48}
          className="ant-avatar"
          src="components/nhguidelogo.jpg"
          style={styles.logoIcon}
        />
        <div>
          <Typography.Title level={3} style={styles.logoText}>NH Guide</Typography.Title>
          <Typography.Text style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '10px' : '14px', fontWeight: 500, lineHeight: 1.3 }}>
            Privacy & Security
          </Typography.Text>
        </div>
      </div>
      <div style={styles.navContainer}>
        {menuItems.map((item) => (
          <motion.div
            key={item.key}
            whileHover={{ scale: 1.05, backgroundColor: theme.colors.navHover }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{ borderRadius: theme.borderRadius.sm }}
          >
            <Link
              to={item.to}
              style={{
                ...styles.navItem,
                ...(item.active ? styles.navItemActive : {}),
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          </motion.div>
        ))}
      </div>
      <div style={{ display: window.innerWidth <= 768 ? 'flex' : 'none', alignItems: 'center' }}>
        <Dropdown
          menu={{ items: mobileMenuItems }}
          trigger={['click']}
          open={menuVisible}
          onOpenChange={(open) => setMenuVisible(open)}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            style={styles.mobileMenuButton}
            size="large"
          />
        </Dropdown>
      </div>
      <Space align="center" size={window.innerWidth <= 768 ? 6 : 20}>
        <Tooltip title="Notifications">
          <Button
            type="text"
            icon={<BellOutlined />}
            size="large"
            style={{ position: 'relative', color: theme.colors.neutral }}
          >
            <Badge
              count={userData?.notifications || 3}
              size="small"
              style={{ backgroundColor: theme.colors.warning, boxShadow: theme.shadows.xs }}
            />
          </Button>
        </Tooltip>
        <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth <= 768 ? '6px' : '12px' }}>
          <Avatar
            icon={<UserOutlined />}
            size={window.innerWidth <= 768 ? 28 : 44}
            src={userData?.avatar}
            style={{ background: theme.colors.primary, boxShadow: theme.shadows.xs }}
          />
          <div>
            <Typography.Text strong style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px', color: theme.colors.darkGray, lineHeight: 1.3 }}>
              {loading ? 'Loading...' : userData?.name || 'User Account'}
            </Typography.Text>
            <br />
            <Typography.Text style={{ fontSize: window.innerWidth <= 768 ? '10px' : '12px', color: theme.colors.neutral, lineHeight: 1.3 }}>
              Secure Account
            </Typography.Text>
          </div>
        </div>
        <Tooltip title="Logout">
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            size="large"
            style={{
              color: theme.colors.error,
              fontWeight: 500,
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              borderRadius: theme.borderRadius.sm,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.colors.navHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            {window.innerWidth > 768 && 'Logout'}
          </Button>
        </Tooltip>
      </Space>
    </motion.div>
  );
});

// Privacy Control Component
const PrivacyControl = memo(({ title, description, enabled, onChange, icon, risk = 'low', disabled = false }) => (
  <motion.div
    style={{
      backgroundColor: theme.colors.white,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      padding: window.innerWidth <= 768 ? '8px' : '24px',
      marginBottom: '8px',
      transition: 'all 0.2s ease',
    }}
    whileHover={{ scale: 1.02, boxShadow: theme.shadows.sm }}
    whileTap={{ scale: 0.98 }}
    transition={{ duration: 0.1 }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth <= 768 ? '6px' : '20px', flex: 1 }}>
        <div style={{
          width: window.innerWidth <= 768 ? '24px' : '48px',
          height: window.innerWidth <= 768 ? '24px' : '48px',
          borderRadius: theme.borderRadius.sm,
          backgroundColor: `${theme.colors.primary}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.colors.info,
          fontSize: window.innerWidth <= 768 ? '12px' : '20px',
        }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <Typography.Text strong style={{ color: theme.colors.darkGray, fontSize: window.innerWidth <= 768 ? '12px' : '16px', lineHeight: 1.4 }}>
              {title}
            </Typography.Text>
            <Tag
              color={risk === 'high' ? 'error' : risk === 'medium' ? 'warning' : 'success'}
              style={{ fontSize: window.innerWidth <= 768 ? '10px' : '12px', lineHeight: 1.4 }}
            >
              {risk.toUpperCase()} RISK
            </Tag>
          </div>
          <Typography.Text style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '11px' : '14px', lineHeight: 1.7 }}>
            {description}
          </Typography.Text>
        </div>
      </div>
      <Switch
        checked={enabled}
        onChange={onChange}
        size="default"
        style={{ marginLeft: window.innerWidth <= 768 ? '6px' : '20px' }}
        disabled={disabled}
      />
    </div>
  </motion.div>
));

const DataPrivacy = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    fullyPrivate: false,
    selectedProviders: false,
    generalProviders: false,
    research: false,
  });
  const [dataExportLoading, setDataExportLoading] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [correctDataModal, setCorrectDataModal] = useState(false);
  const [privacyPolicyModal, setPrivacyPolicyModal] = useState(false);
  const { userData, loading: userLoading, error } = useUserData();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPrivacySettings = async () => {
      setLoading(true);
      try {
        const response = await fetch('/backend/save_privacy_settings.php', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) {
          if (response.status === 401) {
            navigate('/login');
            notification.error({
              message: 'Unauthorized',
              description: 'Please log in to access privacy settings.',
              placement: 'topRight',
              duration: 3,
            });
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data.error) {
          throw new Error(data.error);
        }
        setPrivacySettings({
          fullyPrivate: !!data.fullyPrivate,
          selectedProviders: !!data.selectedProviders,
          generalProviders: !!data.generalProviders,
          research: !!data.research,
        });
      } catch (error) {
        notification.error({
          message: 'Error Loading Privacy Settings',
          description: error.message || 'Failed to load privacy settings.',
          placement: 'topRight',
          duration: 3,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPrivacySettings();
  }, [navigate]);

  const privacyScore = Math.round(
    (Object.values(privacySettings).filter(setting => !setting).length / Object.keys(privacySettings).length) * 100
  );

  const handlePrivacyChange = useCallback(async (setting, value) => {
    const updatedSettings = { ...privacySettings, [setting]: value };
    setLoading(true);
    try {
      const response = await fetch('/backend/save_privacy_settings.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updatedSettings),
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          notification.error({
            message: 'Unauthorized',
            description: 'Please log in to update privacy settings.',
            placement: 'topRight',
            duration: 3,
          });
          return;
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      setPrivacySettings(updatedSettings);
      notification.success({
        message: 'Setting Updated',
        description: 'Your privacy preferences have been saved successfully.',
        placement: 'topRight',
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: 'Error Saving Privacy Settings',
        description: error.message || 'Failed to save privacy settings.',
        placement: 'topRight',
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  }, [privacySettings, navigate]);

  const handleDataExport = useCallback(async () => {
    setDataExportLoading(true);
    try {
      const response = await fetch('/backend/export_data.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          notification.error({
            message: 'Unauthorized',
            description: 'Please log in to export data.',
            placement: 'topRight',
            duration: 3,
          });
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user_data_${userData?.name || 'user'}_${new Date().toISOString()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      notification.success({
        message: 'Export Successful',
        description: 'Your data has been downloaded as a JSON file.',
        placement: 'topRight',
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: 'Export Error',
        description: error.message || 'An error occurred while exporting your data.',
        placement: 'topRight',
        duration: 3,
      });
    } finally {
      setDataExportLoading(false);
    }
  }, [userData, navigate]);

  const handleCorrectData = useCallback(async (values) => {
    try {
      const response = await fetch('/backend/correct_data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(values),
      });
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          notification.error({
            message: 'Unauthorized',
            description: 'Please log in to correct data.',
            placement: 'topRight',
            duration: 3,
          });
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        notification.success({
          message: 'Correction Request Sent',
          description: 'Your data correction request has been submitted successfully.',
          placement: 'topRight',
          duration: 3,
        });
        setCorrectDataModal(false);
        form.resetFields();
      } else {
        throw new Error(data.error || 'Failed to submit correction request.');
      }
    } catch (error) {
      notification.error({
        message: 'Correction Error',
        description: error.message || 'An error occurred while submitting your correction request.',
        placement: 'topRight',
        duration: 3,
      });
    }
  }, [form, navigate]);

  const handleDeleteAccount = useCallback(async () => {
    try {
      const response = await fetch('/backend/delete_account.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          notification.error({
            message: 'Unauthorized',
            description: 'Please log in to delete account.',
            placement: 'topRight',
            duration: 3,
          });
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        notification.success({
          message: 'Deletion Request Sent',
          description: 'Your account deletion request has been processed and will be completed within 30 days.',
          placement: 'topRight',
          duration: 3,
        });
        setDeleteAccountModal(false);
        navigate('/login');
      } else {
        throw new Error(data.error || 'Failed to process account deletion.');
      }
    } catch (error) {
      notification.error({
        message: 'Deletion Error',
        description: error.message || 'An error occurred while processing the deletion request.',
        placement: 'topRight',
        duration: 3,
      });
    }
  }, [navigate]);

  if (error) {
    return (
      <Layout style={styles.layout}>
        <Layout.Content style={{ textAlign: 'center', padding: window.innerWidth <= 768 ? '20px 8px' : '100px 24px' }}>
          <Typography.Text type="danger" style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px', lineHeight: 1.7 }}>
            {error}
          </Typography.Text>
          <Button
            type="primary"
            onClick={() => window.location.reload()}
            style={{ 
              marginTop: 10, 
              display: 'block', 
              margin: '10px auto', 
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              borderRadius: theme.borderRadius.sm,
              background: theme.colors.primary,
              padding: window.innerWidth <= 768 ? '6px 16px' : '8px 24px',
              height: 'auto',
            }}
          >
            Retry
          </Button>
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Layout style={styles.layout}>
      <ModernHeader userData={userData} loading={userLoading} />
      <Layout.Content style={styles.content}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div style={styles.pageHeader} {...animations.slideUp} transition={{ duration: 0.25 }}>
            <div style={styles.progressContainer}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Typography.Text strong style={{ color: theme.colors.darkGray, fontSize: window.innerWidth <= 768 ? '13px' : '16px', lineHeight: 1.4 }}>
                  Privacy Protection Level
                </Typography.Text>
                <Typography.Text strong style={{ color: theme.colors.info, fontSize: window.innerWidth <= 768 ? '16px' : '20px', lineHeight: 1.4 }}>
                  {privacyScore}%
                </Typography.Text>
              </div>
              <Progress
                percent={privacyScore}
                size={[6, window.innerWidth <= 768 ? 10 : 20]}
                strokeColor={{
                  '0%': theme.colors.error,
                  '50%': theme.colors.warning,
                  '100%': theme.colors.success,
                }}
                showInfo={false}
              />
              <Typography.Text style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', marginTop: '6px', lineHeight: 1.7 }}>
                {privacyScore >= 80 ? 'Excellent protection level - Your data is highly secure' :
                 privacyScore >= 60 ? 'Good protection level - Consider reviewing some settings' :
                 'Protection level needs improvement - Please review your privacy settings'}
              </Typography.Text>
            </div>
          </motion.div>
          <motion.div style={styles.sectionCard} {...animations.slideUp} transition={{ duration: 0.25, delay: 0.1 }}>
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Typography.Title
                level={2}
                style={{
                  margin: 0,
                  color: theme.colors.darkGray,
                  fontWeight: 700,
                  fontSize: window.innerWidth <= 768 ? '18px' : '24px',
                  lineHeight: 1.3,
                }}
              >
                Privacy Settings
              </Typography.Title>
              <Button
                type="primary"
                icon={<InfoCircleOutlined />}
                onClick={() => setPrivacyPolicyModal(true)}
                style={{ 
                  borderRadius: theme.borderRadius.sm, 
                  fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                  background: theme.colors.primary,
                  padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                  height: 'auto',
                }}
              >
                View Privacy Policy
              </Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
              <PrivacyControl
                icon={<LockOutlined />}
                title="Fully Private Profile"
                description="My User details and data remain fully private and not shared (if this is selected then your information will not be shared with service providers or third parties)"
                enabled={privacySettings.fullyPrivate}
                onChange={(value) => handlePrivacyChange('fullyPrivate', value)}
                risk="low"
              />
              <PrivacyControl
                icon={<TeamOutlined />}
                title="Share with Selected Providers"
                description="Share My User details and data with parties and service providers that I select"
                enabled={privacySettings.selectedProviders}
                onChange={(value) => handlePrivacyChange('selectedProviders', value)}
                risk="medium"
              />
              <PrivacyControl
                icon={<TeamOutlined />}
                title="Share with General Providers"
                description="Share my User details and data with service providers generally in the homelessness response system to support coordination of services to help me to access resources, services and housing"
                enabled={privacySettings.generalProviders}
                onChange={(value) => handlePrivacyChange('generalProviders', value)}
                risk="medium"
              />
              <PrivacyControl
                icon={<EyeOutlined />}
                title="Share for Research"
                description="Share my User details and data for reporting, research and or analysis about areas of the housing crisis and homelessness response system"
                enabled={privacySettings.research}
                onChange={(value) => handlePrivacyChange('research', value)}
                risk="high"
              />
            </div>
          </motion.div>
          <motion.div style={styles.sectionCard} {...animations.slideUp} transition={{ duration: 0.25, delay: 0.2 }}>
            <div style={{ marginBottom: '16px' }}>
              <Typography.Title
                level={2}
                style={{
                  margin: 0,
                  color: theme.colors.darkGray,
                  fontWeight: 700,
                  fontSize: window.innerWidth <= 768 ? '18px' : '24px',
                  lineHeight: 1.3,
                }}
              >
                Your Data Rights
              </Typography.Title>
              <Typography.Paragraph style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '13px' : '16px', margin: '8px 0 0 0', lineHeight: 1.7 }}>
                Exercise your rights regarding your personal data. These actions help you maintain control over your information.
              </Typography.Paragraph>
            </div>
            <Row gutter={[window.innerWidth <= 768 ? 8 : 24, window.innerWidth <= 768 ? 8 : 24]}>
              <Col xs={24} md={8}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.1 }}>
                  <Card
                    hoverable
                    style={styles.actionCard}
                    styles={{ body: { padding: window.innerWidth <= 768 ? '8px' : '32px' } }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: window.innerWidth <= 768 ? '32px' : '64px',
                        height: window.innerWidth <= 768 ? '32px' : '64px',
                        borderRadius: theme.borderRadius.sm,
                        backgroundColor: `${theme.colors.info}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.colors.info,
                        fontSize: window.innerWidth <= 768 ? '16px' : '28px',
                        margin: '0 auto 8px',
                      }}>
                        <DownloadOutlined />
                      </div>
                      <Typography.Title
                        level={4}
                        style={{
                          margin: '0 0 6px 0',
                          color: theme.colors.darkGray,
                          fontSize: window.innerWidth <= 768 ? '16px' : '20px',
                          lineHeight: 1.3,
                        }}
                      >
                        Export My Data
                      </Typography.Title>
                      <Typography.Text style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', display: 'block', marginBottom: '8px', lineHeight: 1.7 }}>
                        Download a complete copy of all your personal data in a JSON format
                      </Typography.Text>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={dataExportLoading}
                        onClick={handleDataExport}
                        size="large"
                        block
                        style={{ 
                          borderRadius: theme.borderRadius.sm, 
                          fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                          background: theme.colors.primary,
                          padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                          height: 'auto',
                        }}
                      >
                        Export Data
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              </Col>
              <Col xs={24} md={8}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.1 }}>
                  <Card
                    hoverable
                    style={styles.actionCard}
                    styles={{ body: { padding: window.innerWidth <= 768 ? '8px' : '32px' } }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: window.innerWidth <= 768 ? '32px' : '64px',
                        height: window.innerWidth <= 768 ? '32px' : '64px',
                        borderRadius: theme.borderRadius.sm,
                        backgroundColor: `${theme.colors.warning}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.colors.warning,
                        fontSize: window.innerWidth <= 768 ? '16px' : '28px',
                        margin: '0 auto 8px',
                      }}>
                        <EditOutlined />
                      </div>
                      <Typography.Title
                        level={4}
                        style={{
                          margin: '0 0 6px 0',
                          color: theme.colors.darkGray,
                          fontSize: window.innerWidth <= 768 ? '16px' : '20px',
                          lineHeight: 1.3,
                        }}
                      >
                        Correct My Data
                      </Typography.Title>
                      <Typography.Text style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', display: 'block', marginBottom: '8px', lineHeight: 1.7 }}>
                        Submit a request to correct inaccurate or outdated personal information
                      </Typography.Text>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setCorrectDataModal(true)}
                        size="large"
                        block
                        style={{ 
                          borderRadius: theme.borderRadius.sm, 
                          fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                          background: theme.colors.primary,
                          padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                          height: 'auto',
                        }}
                      >
                        Request Correction
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              </Col>
              <Col xs={24} md={8}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.1 }}>
                  <Card
                    hoverable
                    style={{
                      ...styles.actionCard,
                      border: `1px solid ${theme.colors.error}20`,
                    }}
                    styles={{ body: { padding: window.innerWidth <= 768 ? '8px' : '32px' } }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: window.innerWidth <= 768 ? '32px' : '64px',
                        height: window.innerWidth <= 768 ? '32px' : '64px',
                        borderRadius: theme.borderRadius.sm,
                        backgroundColor: `${theme.colors.error}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.colors.error,
                        fontSize: window.innerWidth <= 768 ? '16px' : '28px',
                        margin: '0 auto 8px',
                      }}>
                        <DeleteOutlined />
                      </div>
                      <Typography.Title
                        level={4}
                        style={{
                          margin: '0 0 6px 0',
                          color: theme.colors.darkGray,
                          fontSize: window.innerWidth <= 768 ? '16px' : '20px',
                          lineHeight: 1.3,
                        }}
                      >
                        Delete My Account
                      </Typography.Title>
                      <Typography.Text style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', display: 'block', marginBottom: '8px', lineHeight: 1.7 }}>
                        Permanently delete your account and all associated data from our servers
                      </Typography.Text>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setDeleteAccountModal(true)}
                        size="large"
                        block
                        style={{ 
                          borderRadius: theme.borderRadius.sm, 
                          fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                          padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                          height: 'auto',
                        }}
                      >
                        Delete Account
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </motion.div>
      </Layout.Content>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DeleteOutlined style={{ color: theme.colors.error, fontSize: window.innerWidth <= 768 ? '14px' : '18px' }} />
            <span style={{ fontSize: window.innerWidth <= 768 ? '14px' : '18px', fontWeight: 600 }}>Delete Your Account</span>
          </div>
        }
        open={deleteAccountModal}
        onCancel={() => setDeleteAccountModal(false)}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => setDeleteAccountModal(false)} 
            size="large" 
            style={{ 
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              borderRadius: theme.borderRadius.sm,
              padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
              height: 'auto',
            }}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={handleDeleteAccount}
            size="large"
            style={{ 
              borderRadius: theme.borderRadius.sm, 
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              background: theme.colors.error,
              padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
              height: 'auto',
            }}
          >
            Confirm Deletion
          </Button>,
        ]}
        width={window.innerWidth <= 768 ? '90%' : 600}
        style={{ padding: window.innerWidth <= 768 ? '4px' : '24px' }}
        styles={{ body: { backgroundColor: theme.colors.white, borderRadius: theme.borderRadius.md } }}
      >
        <Alert
          message="Irreversible Action"
          description="Deleting your account will permanently remove all your data from our servers. This action cannot be undone."
          type="warning"
          showIcon
          style={{ marginBottom: '8px', borderRadius: theme.borderRadius.sm, fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}
        />
        <Divider style={{ margin: '8px 0' }} />
        <Typography.Text style={{ fontSize: window.innerWidth <= 768 ? '12px' : '15px', lineHeight: 1.7, color: theme.colors.neutral }}>
          Are you sure you want to permanently delete your account? All your personal data, history, and settings will be removed from our servers within 30 days.
          <br /><br />
          <strong>This includes:</strong>
          <ul style={{ marginTop: '8px', paddingLeft: '16px', fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
            <li>Personal profile information</li>
            <li>Service history and connections</li>
            <li>Privacy settings and preferences</li>
            <li>All uploaded documents and files</li>
          </ul>
        </Typography.Text>
      </Modal>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EditOutlined style={{ color: theme.colors.warning, fontSize: window.innerWidth <= 768 ? '14px' : '18px' }} />
            <span style={{ fontSize: window.innerWidth <= 768 ? '14px' : '18px', fontWeight: 600 }}>Correct Your Data</span>
          </div>
        }
        open={correctDataModal}
        onCancel={() => {
          setCorrectDataModal(false);
          form.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setCorrectDataModal(false);
              form.resetFields();
            }}
            size="large"
            style={{ 
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              borderRadius: theme.borderRadius.sm,
              padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
              height: 'auto',
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => form.submit()}
            size="large"
            style={{ 
              borderRadius: theme.borderRadius.sm, 
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              background: theme.colors.primary,
              padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
              height: 'auto',
            }}
          >
            Submit Correction
          </Button>,
        ]}
        width={window.innerWidth <= 768 ? '90%' : 600}
        style={{ padding: window.innerWidth <= 768 ? '4px' : '24px' }}
        styles={{ body: { backgroundColor: theme.colors.white, borderRadius: theme.borderRadius.md } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCorrectData}
        >
          <Alert
            message="Data Correction Request"
            description="Please provide the correct information below. Our team will review your request and update your profile accordingly."
            type="info"
            showIcon
            style={{ marginBottom: '8px', borderRadius: theme.borderRadius.sm, fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}
          />
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: false, message: 'Please enter your full name' }]}
          >
            <Input placeholder="Enter your full name" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px', borderRadius: theme.borderRadius.sm }} />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[{ required: false, type: 'email', message: 'Please enter a valid email address' }]}
          >
            <Input placeholder="Enter your email address" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px', borderRadius: theme.borderRadius.sm }} />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ required: false, message: 'Please enter your phone number' }]}
          >
            <Input placeholder="Enter your phone number" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px', borderRadius: theme.borderRadius.sm }} />
          </Form.Item>
          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: false, message: 'Please enter your address' }]}
          >
            <Input.TextArea placeholder="Enter your address" rows={3} style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px', borderRadius: theme.borderRadius.sm }} />
          </Form.Item>
          <Form.Item
            name="documents"
            label="Supporting Documents (optional)"
          >
            <Upload
              beforeUpload={() => false}
              accept=".pdf,.doc,.docx,.jpg,.png"
              multiple
              maxCount={3}
            >
              <Button 
                icon={<UploadOutlined />} 
                style={{ 
                  fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                  borderRadius: theme.borderRadius.sm,
                  padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
                  height: 'auto',
                }}
              >
                Upload Documents
              </Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name="notes"
            label="Additional Notes"
            rules={[{ required: false }]}
          >
            <Input.TextArea 
              placeholder="Any additional information about the correction" 
              rows={4} 
              style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px', borderRadius: theme.borderRadius.sm }} 
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <InfoCircleOutlined style={{ color: theme.colors.info, fontSize: window.innerWidth <= 768 ? '14px' : '18px' }} />
            <span style={{ fontSize: window.innerWidth <= 768 ? '14px' : '18px', fontWeight: 600 }}>NH GUIDE Data Sharing and Release of Information</span>
          </div>
        }
        open={privacyPolicyModal}
        onCancel={() => setPrivacyPolicyModal(false)}
        footer={[ 
          <Button
            key="close"
            onClick={() => setPrivacyPolicyModal(false)}
            size="large"
            style={{ 
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              borderRadius: theme.borderRadius.sm,
              padding: window.innerWidth <= 768 ? '6px 12px' : '8px 16px',
              height: 'auto',
            }}
          >
            Close
          </Button>,
        ]}
        width={window.innerWidth <= 768 ? '90%' : 800}
        style={{ padding: window.innerWidth <= 768 ? '4px' : '24px' }}
        styles={{ body: { backgroundColor: theme.colors.white, borderRadius: theme.borderRadius.md } }}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: window.innerWidth <= 768 ? '8px' : '16px' }}>
          <Typography.Title
            level={4}
            style={{
              color: theme.colors.darkGray,
              marginBottom: '8px',
              fontSize: window.innerWidth <= 768 ? '16px' : '18px',
              lineHeight: 1.3,
              fontWeight: 600,
            }}
          >
            NH GUIDE Data Sharing and Release of Information Policy
          </Typography.Title>
          <Typography.Paragraph style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', lineHeight: 1.7 }}>
            The NH GUIDE allows you (“user”) to share your information, including your protected personally identifiable information (“pii”) with third party organizations, participating service providers and the housing crisis response system.
          </Typography.Paragraph>
          <Typography.Title
            level={5}
            style={{
              color: theme.colors.darkGray,
              margin: '8px 0 6px',
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              lineHeight: 1.3,
              fontWeight: 600,
            }}
          >
            Purposes for sharing information
          </Typography.Title>
          <Typography.Paragraph style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', lineHeight: 1.7 }}>
            User information shared in the NH GUIDE (“app”) helps:
            <ul style={{ marginTop: '6px', paddingLeft: '16px', fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
              <li>Organizations and service providers to coordinate and connect you with housing programs, services, or resources including shelter and housing solutions.</li>
              <li>To support research about housing, homelessness services and the housing crisis response system.</li>
              <li>To improve and advance the provision of services and the housing crisis response system.</li>
            </ul>
          </Typography.Paragraph>
          <Typography.Title
            level={5}
            style={{
              color: theme.colors.darkGray,
              margin: '8px 0 6px',
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              lineHeight: 1.3,
              fontWeight: 600,
            }}
          >
            Parties for information sharing
          </Typography.Title>
          <Typography.Paragraph style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', lineHeight: 1.7 }}>
            The services and support that are provided by organizations, service providers and agencies in the housing crisis response system include homelessness prevention and assistance; shelter and housing solutions; medical, mental and behavioral health; and information and software related to housing and housing services.
          </Typography.Paragraph>
          <Typography.Paragraph style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', lineHeight: 1.7 }}>
            Any user information that is shared in the NH GUIDE (“app”) is shared in compliance with Federal, State, local or municipal government regulations, legal regulations or court orders, and to preserve or protect personal and/or public safety.
          </Typography.Paragraph>
          <Typography.Title
            level={5}
            style={{
              color: theme.colors.darkGray,
              margin: '8px 0 6px',
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              lineHeight: 1.3,
              fontWeight: 600,
            }}
          >
            Types of Information shared
          </Typography.Title>
          <Typography.Paragraph style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', lineHeight: 1.7 }}>
            The information you share in the NH GUIDE app may include information about programs and services you receive or have previously received. Your
            <ul style={{ marginTop: '6px', paddingLeft: '16px', fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
              <li>Legal First and Last name</li>
              <li>Other Chosen Names</li>
              <li>Location and/or Home address</li>
              <li>Personal email address</li>
              <li>Social security number</li>
              <li>Identification Details (Passport number, Driver’s license number)</li>
              <li>Date of birth</li>
              <li>Telephone number</li>
              <li>Medical information</li>
              <li>Criminal Justice System history/details</li>
              <li>Photos/images</li>
            </ul>
          </Typography.Paragraph>
          <Typography.Title
            level={5}
            style={{
              color: theme.colors.darkGray,
              margin: '8px 0 6px',
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              lineHeight: 1.3,
              fontWeight: 600,
            }}
          >
            Opting not to share information
          </Typography.Title>
          <Typography.Paragraph style={{ color: theme.colors.neutral, fontSize: window.innerWidth <= 768 ? '12px' : '14px', lineHeight: 1.7 }}>
            If you choose not to share your information with other parties through NH GUIDE, your information will not be shared for marketing purposes.
          </Typography.Paragraph>
          <Divider style={{ margin: '8px 0' }} />
        </div>
      </Modal>
    </Layout>
  );
};

export default DataPrivacy;